import 'twin.macro';
import React from 'react';
import TimePicker from '@teetsh/app/src/components/Form/TimePicker2';
import { Classroom } from '@teetsh/app/src/types/classroom';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import { EDTView } from '@teetsh/app/src/types/edt';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import { useTranslation } from 'react-i18next';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import Input from '@teetsh/app/src/components/Form/Input2';
import Select from 'components/Form/Select2';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';

interface Props {
  displayedClassrooms: string[];
  classrooms: Classroom[];
  onZoomChange: (n: number) => void;
  step: number;
  currentView: EDTView;
  onChangeDisplayedClassrooms: (s: string[]) => void;
  onUpdateStartDate: (v: Date) => void;
  onUpdateEndDate: (v: Date) => void;
  onUpdateView: (v: EDTView) => void;
  startTime: Date;
  endTime: Date;
}

export function EDTToolbarActions(props: Props) {
  const {
    step,
    onUpdateStartDate,
    displayedClassrooms,
    currentView,
    onUpdateEndDate,
    onChangeDisplayedClassrooms,
    startTime,
    onZoomChange,
    classrooms,
    endTime,
    onUpdateView,
  } = props;
  const { t } = useTranslation(['emploiDuTemps']);

  const isMobile = useIsBelowBreakpoint('md');
  return (
    <div>
      <div tw="grid grid-cols-2 gap-2">
        <TimePicker
          id="debut"
          label={t('emploiDuTemps:sidebar.startHour')}
          value={startTime}
          onChange={onUpdateStartDate}
        />
        <TimePicker
          id="fin"
          label={t('emploiDuTemps:sidebar.endHour')}
          value={endTime}
          onChange={onUpdateEndDate}
        />
      </div>

      <FormGroup label={t('emploiDuTemps:sidebar.zoom')} tw="flex-1">
        <div tw="flex gap-2 flex-1">
          <div tw="flex-grow w-full">
            <Input
              value={`${Math.round(((-1 * (step - 20)) / 10) * 100)}%`}
              onChange={() => {}}
              readonly
            />
          </div>
          <Button
            icon={MinusIcon}
            onClick={() => onZoomChange(Math.min(15, step + 2.5))}
            disabled={step >= 15}
            tw="border-neutral-quiet"
            aria-label={t('emploiDuTemps:sidebar.zoomOut')}
          />
          <Button
            onClick={() => onZoomChange(Math.max(2.5, step - 2.5))}
            tw="border-neutral-quiet"
            icon={PlusIcon}
            disabled={step <= 2.5}
            aria-label={t('emploiDuTemps:sidebar.zoomIn')}
          />
        </div>
      </FormGroup>

      {!isMobile && (
        <FormGroup label={t('emploiDuTemps:sidebar.view')} tw="flex-1">
          <Select<EDTView>
            onChange={v => onUpdateView(v)}
            options={[
              {
                value: 'week',
                label: t('emploiDuTemps:sidebar.week'),
              },
              {
                value: 'day',
                label: t('emploiDuTemps:sidebar.day'),
              },
            ]}
            value={currentView}
          />
        </FormGroup>
      )}

      {classrooms.length > 1 && (
        <FormGroup label={t('emploiDuTemps:sidebar.displayedClassrooms')}>
          {classrooms.map(cl => (
            <Checkbox
              key={cl.id}
              label={cl.name}
              checked={displayedClassrooms.includes(cl.id)}
              onChange={() => {
                if (displayedClassrooms.includes(cl.id)) {
                  return onChangeDisplayedClassrooms(
                    displayedClassrooms.filter(c => c !== cl.id),
                  );
                }
                return onChangeDisplayedClassrooms([
                  ...displayedClassrooms,
                  cl.id,
                ]);
              }}
            />
          ))}
        </FormGroup>
      )}
    </div>
  );
}
